// ===============================================================
// fcmRegistration.js — register/unregister FCM token via Cloud Functions
// ===============================================================

import { Capacitor } from "@capacitor/core";
import { FirebaseMessaging } from "@capacitor-firebase/messaging";
import { getFunctions, httpsCallable } from "firebase/functions";
import { auth } from "../firebase";

const functions = getFunctions(undefined, "us-central1");

function getFcmPlatform() {
  return Capacitor.getPlatform() === "android" ? "android" : "web";
}

function extractFcmToken(result) {
  if (!result || typeof result !== "object") return "";
  const raw = result.token ?? result.value;
  return typeof raw === "string" ? raw.trim() : "";
}

/**
 * Request notification permission, obtain FCM token, save via registerFcmToken.
 * Best-effort; does not throw.
 * @param {import("firebase/auth").User | null | undefined} user
 */
export async function registerFcmForCurrentUser(user) {
  if (!user?.uid) return;

  try {
    const perm = await FirebaseMessaging.requestPermissions();
    if (perm?.receive !== "granted") {
      return { ok: false, reason: "permission_denied" };
    }

    const tokenResult = await FirebaseMessaging.getToken();
    const token = extractFcmToken(tokenResult);
    if (!token) {
      return { ok: false, reason: "no_token" };
    }

    const registerFn = httpsCallable(functions, "registerFcmToken");
    await registerFn({
      token,
      platform: getFcmPlatform(),
    });

    return { ok: true };
  } catch (err) {
    console.warn("registerFcmForCurrentUser failed:", err);
    return { ok: false, error: err };
  }
}

/**
 * Remove current FCM token from the server (best-effort; does not throw).
 */
export async function unregisterFcmForCurrentUser() {
  try {
    let token = "";
    try {
      const tokenResult = await FirebaseMessaging.getToken();
      token = extractFcmToken(tokenResult);
    } catch {
      /* token may be unavailable */
    }

    if (!token) return;

    const unregisterFn = httpsCallable(functions, "unregisterFcmToken");
    await unregisterFn({ token });
  } catch (err) {
    console.warn("unregisterFcmForCurrentUser failed:", err);
  }
}

// ===============================================================
// Token-refresh listener
// ===============================================================
// No FCM token-refresh handling existed before this: a token rotated
// mid-session was only picked up on the next onAuthStateChanged transition
// (normally a full app restart / re-login), leaving the backend's stored
// token stale in the meantime. This reuses the exact same
// registerFcmToken Callable contract as initial registration - it never
// writes a token document directly.
let listenerAttached = false;
let listenerHandle = null;
let lastSyncedToken = "";

async function handleTokenReceived(event) {
  // Never log or display the token itself - only whether a rotation was
  // detected and resynced.
  const token = typeof event?.token === "string" ? event.token.trim() : "";
  if (!token || token === lastSyncedToken) return; // no real change / duplicate event

  const currentUser = auth.currentUser;
  if (!currentUser?.uid) {
    // No authenticated user right now (e.g. logged out): do not register a
    // token for nobody. The next login's own registerFcmForCurrentUser()
    // call re-fetches the current token via FirebaseMessaging.getToken()
    // anyway, so nothing is lost.
    return;
  }

  const result = await registerFcmForCurrentUser(currentUser);
  if (result?.ok) {
    lastSyncedToken = token;
  }
}

/**
 * Starts listening for FCM token rotation and re-synchronizes the new
 * token through the existing registerFcmToken Callable. Safe to call
 * multiple times (e.g. across repeated AuthProvider mounts) - only the
 * first call actually attaches the native listener, preventing duplicate
 * registrations for the same rotation event.
 */
export async function startFcmTokenRefreshListener() {
  if (listenerAttached) return;
  listenerAttached = true;

  try {
    listenerHandle = await FirebaseMessaging.addListener(
      "tokenReceived",
      handleTokenReceived
    );
  } catch (err) {
    console.warn("startFcmTokenRefreshListener failed:", err);
    listenerAttached = false;
  }
}

/** Stops the token-refresh listener, if attached. */
export async function stopFcmTokenRefreshListener() {
  if (listenerHandle) {
    await listenerHandle.remove();
    listenerHandle = null;
  }
  listenerAttached = false;
}

/** Test-only: resets module-level listener state between test cases. */
export function __resetFcmTokenRefreshListenerForTests() {
  listenerAttached = false;
  listenerHandle = null;
  lastSyncedToken = "";
}
